/**
 * ServiceDetailInline — Inline-aufklappbare Detail-View pro Service-Zeile.
 *
 * Wird aus Services.tsx unterhalb der ausgewaehlten Tabellen-Zeile gerendert.
 * Tabs: Übersicht | Verfügbarkeit | Aktivität.
 *
 * Im Gegensatz zum Drawer kein position:fixed — die Komponente liegt direkt
 * im Tabellen-DOM (ein <tr> mit colspan).
 */

import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { modeAwareEndpoint } from '../utils/cockpitMode'
import { displayIdentity, isItsceServiceAccount } from '../utils/identityDisplay'

interface HealthBar {
  t: number
  status: 'up' | 'down' | 'partial' | 'unknown'
  value?: number
}

interface Probe {
  url: string
  label: string
  is_primary: boolean
  up?: boolean | null
  uptime_24h?: number | null
  uptime_7d?: number | null
  uptime_30d?: number | null
  latency_sec?: number | null
  last_check_unix?: number | null
  bars?: HealthBar[]
}

interface ServiceDetail {
  plan_id: string
  plan_name: string
  item_code: string
  item_name: string
  variant_name: string
  description: string
  rate: number
  currency: string
  billing_interval: string
  portal_group: string
  portal_category: string
  erpnext_subscription_id: string
  start_date: string
  status: string
  qty: number
  probes: Probe[]
}

interface Props {
  orgSlug: string
  planId: string
}

type Tab = 'overview' | 'health' | 'activity' | 'mailboxes' | 'access' | 'teams' | 'erpnext' | 'erp_users' | 'web_admins' | 'aria_quota'

// Stub-Schemata fuer Service-spezifische Verwaltung (TQ #5122 Phase 1).
// Alle drei kommen mit data_source='stub' und leerer Liste solange die
// Live-Integration (Frappe / WP-REST / Cortex) noch nicht verdrahtet ist.
interface ERPUser {
  email: string
  full_name: string
  enabled: boolean
  roles: string[]
  last_login?: string
}
interface ERPUsersResponse {
  slug: string
  plan_id: string
  item_code: string
  data_source: 'stub' | 'live'
  users: ERPUser[]
  note?: string
}
interface WPUser {
  id: number
  login: string
  email: string
  name: string
  role: string
}
interface WebAdminsResponse {
  slug: string
  plan_id: string
  item_code: string
  data_source: 'stub' | 'live'
  site_url: string
  admins: WPUser[]
  note?: string
}
interface AriaUserQuota {
  user_sub: string
  user_email: string
  tokens_today: number
  tokens_total: number
  per_user_limit?: number
}
interface AriaQuotaResponse {
  slug: string
  plan_id: string
  item_code: string
  data_source: 'stub' | 'live'
  tenant_budget_daily: number
  tenant_used_today: number
  users: AriaUserQuota[]
  note?: string
}

// Group-Folder + ACL Schema fuer /api/org/:slug/services/:plan_id/storage/folders
// (TQ #5121). Phase 1: data_source='stub' -> folders = [].
interface StorageGroupFolderACL {
  team: string
  read: boolean
  update: boolean
  share: boolean
  delete: boolean
}
interface StorageGroupFolder {
  id: number
  name: string
  size?: number
  quota_bytes?: number
  acls: StorageGroupFolderACL[]
}
interface StorageFoldersResponse {
  slug: string
  plan_id: string
  item_code: string
  data_source: 'stub' | 'live'
  folders: StorageGroupFolder[]
  note?: string
}

// Schema des Backend-Endpoints /api/org/:slug/services/:plan_id/erpnext
// (TQ #5106). Felder sind optional, weil Phase 1 nur "stub"-Daten liefert —
// das Frontend zeigt dann einen klaren "noch nicht verfuegbar"-Hinweis statt
// roter Fehlerleiste. Phase 2 ergaenzt Live-Werte aus der Tenant-ERPNext.
interface ERPNextDrawerData {
  slug: string
  plan_id: string
  item_code: string
  erpnext_url: string
  data_source: 'stub' | 'live'
  erpnext_version?: string
  frappe_version?: string
  last_migration_at?: string
  last_migration_ok?: boolean
  active_sessions?: number
  custom_doctypes?: number
  last_backup_at?: string
  last_backup_ok?: boolean
  note?: string
}

interface TeamMember {
  keycloak_id: string
  email: string
  first_name?: string
  last_name?: string
  account_type: 'person' | 'service' | 'mailbox'
  has_membership: boolean
}

interface TeamWithMembers {
  id: string
  name: string
  path: string
  members: TeamMember[]
}

interface AccessMember {
  keycloak_id: string
  email: string
  first_name?: string
  last_name?: string
  account_type: 'person' | 'service' | 'mailbox'
  has_access: boolean
  is_owner: boolean
  // TQ #5897 — Reconciler-Sync-Status. NULL/leer = noch nie reconciled
  // (pending). 'success' = ok. 'error' = failed, Retry-Button erscheint.
  // 'needs_attention' = staff sollte schauen.
  sync_status?: string | null
  retry_count?: number
  last_event_id?: number
}

interface Mailbox {
  address: string
  display_name?: string
  aliases?: string[]
  quota_bytes?: number
  type?: string
  description?: string
}

interface CreateMailboxResp {
  success: boolean
  address: string
  initial_password: string
  message?: string
}

const formatBytes = (n: number): string => {
  if (!n || n <= 0) return '–'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let val = n
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024
    i++
  }
  return `${val.toFixed(val >= 100 ? 0 : 1)} ${units[i]}`
}

export default function ServiceDetailInline({ orgSlug, planId }: Props) {
  const { api: keycloakApi } = useAuth()
  const [tab, setTab] = useState<Tab>('overview')
  const [detail, setDetail] = useState<ServiceDetail | null>(null)
  const [healthProbes, setHealthProbes] = useState<Probe[] | null>(null)
  const [mailboxes, setMailboxes] = useState<Mailbox[] | null>(null)
  const [mailboxesError, setMailboxesError] = useState<string | null>(null)
  const [mailDomain, setMailDomain] = useState<string>('')
  // Storage-Folders im Teams-Tab (TQ #5121 Phase 1)
  const [storageFolders, setStorageFolders] = useState<StorageFoldersResponse | null>(null)
  const [storageFoldersError, setStorageFoldersError] = useState<string | null>(null)
  // Service-spezifische Verwaltungs-Tabs (TQ #5122 Phase 1)
  const [erpUsers, setERPUsers] = useState<ERPUsersResponse | null>(null)
  const [erpUsersError, setERPUsersError] = useState<string | null>(null)
  const [webAdmins, setWebAdmins] = useState<WebAdminsResponse | null>(null)
  const [webAdminsError, setWebAdminsError] = useState<string | null>(null)
  const [ariaQuota, setAriaQuota] = useState<AriaQuotaResponse | null>(null)
  const [ariaQuotaError, setAriaQuotaError] = useState<string | null>(null)
  // Per-User-Limit Inline-Edit (TQ #5770 Phase 2)
  const [ariaEditSub, setAriaEditSub] = useState<string | null>(null)
  const [ariaEditValue, setAriaEditValue] = useState<string>('')
  const [ariaSaving, setAriaSaving] = useState(false)
  // ERPNext-Tab (TQ #5106)
  const [erpnext, setERPNext] = useState<ERPNextDrawerData | null>(null)
  const [erpnextError, setERPNextError] = useState<string | null>(null)
  const [erpnextLoading, setERPNextLoading] = useState(false)
  // Postfach anlegen / loeschen / Passwort zuruecksetzen
  const [showCreate, setShowCreate] = useState(false)
  const [createLocal, setCreateLocal] = useState('')
  const [createDisplay, setCreateDisplay] = useState('')
  const [createPending, setCreatePending] = useState(false)
  const [createdInfo, setCreatedInfo] = useState<CreateMailboxResp | null>(null)
  const [deletePending, setDeletePending] = useState<string | null>(null)
  const [resetPending, setResetPending] = useState<string | null>(null)
  const [resetInfo, setResetInfo] = useState<{ address: string; password: string } | null>(null)
  // Edit-Modal (Quota + Aliases) — TQ #5107 Phase 2
  const [editMailbox, setEditMailbox] = useState<Mailbox | null>(null)
  const [editQuotaInput, setEditQuotaInput] = useState<string>('') // GB als String, leer = unbegrenzt
  const [editAliasesText, setEditAliasesText] = useState<string>('') // Zeile pro Alias
  const [editPending, setEditPending] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  // Zugriff-Tab
  const [accessMembers, setAccessMembers] = useState<AccessMember[] | null>(null)
  const [accessMessage, setAccessMessage] = useState<string>('')
  const [accessGroupPath, setAccessGroupPath] = useState<string>('')
  const [accessError, setAccessError] = useState<string | null>(null)
  const [accessTogglePending, setAccessTogglePending] = useState<string | null>(null)
  // TQ #5897 — Manual-Retry pro service_access_events.id
  const [retryPendingEventId, setRetryPendingEventId] = useState<number | null>(null)
  // Teams-Tab (Storage-Service)
  const [teams, setTeams] = useState<TeamWithMembers[] | null>(null)
  const [teamsError, setTeamsError] = useState<string | null>(null)
  const [teamTogglePending, setTeamTogglePending] = useState<string | null>(null) // key = teamId:userId
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const r = await keycloakApi!.get(
          modeAwareEndpoint(`services/${encodeURIComponent(planId)}`, orgSlug)
        )
        if (cancelled) return
        setDetail(r.data?.service ?? null)
      } catch (e) {
        console.error('service detail load failed', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [orgSlug, planId, keycloakApi])

  useEffect(() => {
    if (tab !== 'health' || !detail) return
    let cancelled = false
    async function loadHealth() {
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/health`
        )
        if (cancelled) return
        setHealthProbes(r.data?.health?.probes ?? [])
      } catch (e) {
        console.error('health load failed', e)
        setHealthProbes([])
      }
    }
    void loadHealth()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  // Service-spezifische Verwaltungs-Tabs Loader (TQ #5122 Phase 1)
  useEffect(() => {
    if (tab !== 'erp_users' || !detail) return
    let cancelled = false
    ;(async () => {
      setERPUsers(null); setERPUsersError(null)
      try {
        const r = await keycloakApi!.get(`/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/erp/users`)
        if (cancelled) return
        setERPUsers(r.data as ERPUsersResponse)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setERPUsersError(err.response?.data?.error ?? 'ERP-User-Liste konnte nicht geladen werden.')
      }
    })()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  useEffect(() => {
    if (tab !== 'web_admins' || !detail) return
    let cancelled = false
    ;(async () => {
      setWebAdmins(null); setWebAdminsError(null)
      try {
        const r = await keycloakApi!.get(`/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/web/admins`)
        if (cancelled) return
        setWebAdmins(r.data as WebAdminsResponse)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setWebAdminsError(err.response?.data?.error ?? 'WP-Admin-Liste konnte nicht geladen werden.')
      }
    })()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  useEffect(() => {
    if (tab !== 'aria_quota' || !detail) return
    let cancelled = false
    ;(async () => {
      setAriaQuota(null); setAriaQuotaError(null)
      try {
        const r = await keycloakApi!.get(`/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/aria/quota`)
        if (cancelled) return
        setAriaQuota(r.data as AriaQuotaResponse)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setAriaQuotaError(err.response?.data?.error ?? 'Aria-Quota konnte nicht geladen werden.')
      }
    })()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  // Per-User-Limit setzen (TQ #5770 Phase 2). leeres Feld -> Limit entfernen.
  const saveAriaUserLimit = async (userSub: string) => {
    const raw = ariaEditValue.trim().replace(/[.\s]/g, '')
    let maxPerDay: number | null = null
    if (raw !== '') {
      const n = Number(raw)
      if (!Number.isFinite(n) || n < 0) {
        setAriaQuotaError('Limit muss eine Zahl >= 0 sein (leer = kein Limit).')
        return
      }
      maxPerDay = Math.floor(n)
    }
    setAriaSaving(true); setAriaQuotaError(null)
    try {
      await keycloakApi!.put(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/aria/quota/users/${encodeURIComponent(userSub)}`,
        { max_tokens_per_day: maxPerDay },
      )
      setAriaEditSub(null); setAriaEditValue('')
      const r = await keycloakApi!.get(`/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/aria/quota`)
      setAriaQuota(r.data as AriaQuotaResponse)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setAriaQuotaError(err.response?.data?.error ?? 'Limit konnte nicht gespeichert werden.')
    } finally {
      setAriaSaving(false)
    }
  }

  // Storage-Folders Loader (TQ #5121) — laedt zusammen mit dem teams-Tab.
  useEffect(() => {
    if (tab !== 'teams' || !detail) return
    if (!detail.item_code?.startsWith('WORKSPACE-STORAGE')) return
    let cancelled = false
    async function loadStorageFolders() {
      setStorageFolders(null)
      setStorageFoldersError(null)
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/storage/folders`
        )
        if (cancelled) return
        setStorageFolders(r.data as StorageFoldersResponse)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setStorageFoldersError(err.response?.data?.error ?? 'Folders konnten nicht geladen werden.')
      }
    }
    void loadStorageFolders()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  // ERPNext-Tab Loader (TQ #5106) — nur fuer WORKSPACE-ERP* Services aktiv.
  useEffect(() => {
    if (tab !== 'erpnext' || !detail) return
    let cancelled = false
    async function loadERPNext() {
      setERPNext(null)
      setERPNextError(null)
      setERPNextLoading(true)
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/erpnext`
        )
        if (cancelled) return
        setERPNext(r.data as ERPNextDrawerData)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setERPNextError(err.response?.data?.error ?? 'ERPNext-Daten konnten nicht geladen werden.')
      } finally {
        if (!cancelled) setERPNextLoading(false)
      }
    }
    void loadERPNext()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  // Postfächer-Tab Loader (TQ #5102) — nur fuer WORKSPACE-MAIL* Services aktiv.
  useEffect(() => {
    if (tab !== 'mailboxes' || !detail) return
    let cancelled = false
    async function loadMailboxes() {
      setMailboxes(null)
      setMailboxesError(null)
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes`
        )
        if (cancelled) return
        setMailboxes(r.data?.mailboxes ?? [])
        setMailDomain(r.data?.domain ?? '')
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setMailboxesError(err.response?.data?.error ?? 'Postfächer konnten nicht geladen werden.')
        setMailboxes([])
      }
    }
    void loadMailboxes()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  const isMailService = !!detail?.item_code && detail.item_code.startsWith('WORKSPACE-MAIL')
  const isStorageService = !!detail?.item_code && detail.item_code.startsWith('WORKSPACE-STORAGE')
  const isERPService = !!detail?.item_code && detail.item_code.startsWith('WORKSPACE-ERP')
  const isWebService = !!detail?.item_code && detail.item_code.startsWith('WORKSPACE-WEB')
  // Aria steckt entweder in WORKSPACE-*-ARIA Plaenen oder MCP-ARIA Plaenen.
  const isAriaService = !!detail?.item_code && (
    detail.item_code.includes('ARIA') || detail.item_code.startsWith('MCP-ARIA')
  )
  const isBaseService = !!detail?.item_code && (
    detail.item_code.startsWith('WORKSPACE-BASE') ||
    detail.item_code.startsWith('MCP-BITWARDEN')
  )

  // Teams-Tab Loader — nur fuer Storage-Service aktiv (NC Group-Folders/ACLs).
  useEffect(() => {
    if (tab !== 'teams' || !detail) return
    let cancelled = false
    async function loadTeams() {
      setTeams(null)
      setTeamsError(null)
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/teams`
        )
        if (cancelled) return
        setTeams(r.data?.teams ?? [])
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setTeamsError(err.response?.data?.error ?? 'Teams konnten nicht geladen werden.')
        setTeams([])
      }
    }
    void loadTeams()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, keycloakApi])

  async function toggleTeamMember(teamId: string, member: TeamMember) {
    const key = `${teamId}:${member.keycloak_id}`
    setTeamTogglePending(key)
    setTeamsError(null)
    try {
      const url = `/api/org/${orgSlug}/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(member.keycloak_id)}`
      if (member.has_membership) {
        await keycloakApi!.delete(url)
      } else {
        await keycloakApi!.put(url, {})
      }
      // Reload
      const r = await keycloakApi!.get(`/api/org/${orgSlug}/teams`)
      setTeams(r.data?.teams ?? [])
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setTeamsError(err.response?.data?.error ?? 'Aenderung fehlgeschlagen.')
    } finally {
      setTeamTogglePending(null)
    }
  }

  // Zugriff-Tab Loader — alle Services die eine /services/* KC-Group haben.
  // Backend liefert leeres members[] + message bei Items ohne Group (Lizenzen, Domain).
  useEffect(() => {
    if (tab !== 'access' || !detail) return
    let cancelled = false
    async function loadAccess() {
      setAccessMembers(null)
      setAccessError(null)
      setAccessMessage('')
      try {
        const r = await keycloakApi!.get(
          `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/access`
        )
        if (cancelled) return
        setAccessMembers(r.data?.members ?? [])
        setAccessGroupPath(r.data?.group_path ?? '')
        if (r.data?.message) setAccessMessage(r.data.message as string)
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        setAccessError(err.response?.data?.error ?? 'Zugriffsliste konnte nicht geladen werden.')
        setAccessMembers([])
      }
    }
    void loadAccess()
    return () => { cancelled = true }
  }, [tab, detail, orgSlug, planId, keycloakApi])

  async function toggleAccess(member: AccessMember) {
    if (member.is_owner) return // Owner darf nicht entzogen werden
    setAccessTogglePending(member.keycloak_id)
    setAccessError(null)
    try {
      const url = `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/access/${encodeURIComponent(member.keycloak_id)}`
      if (member.has_access) {
        await keycloakApi!.delete(url)
      } else {
        await keycloakApi!.put(url, {})
      }
      // Reload um den echten KC-Stand zu sehen
      const r = await keycloakApi!.get(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/access`
      )
      setAccessMembers(r.data?.members ?? [])
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setAccessError(err.response?.data?.error ?? 'Aenderung fehlgeschlagen.')
    } finally {
      setAccessTogglePending(null)
    }
  }

  // TQ #5897 — Manual-Retry fuer fehlgeschlagene Reconciler-Events.
  // POST /api/staff/reconciler/retry/:event_id setzt next_retry_at=NOW +
  // reconciler_status=NULL, der Worker picked das sofort wieder auf.
  async function retryEvent(eventId: number) {
    setRetryPendingEventId(eventId)
    setAccessError(null)
    try {
      await keycloakApi!.post(`/api/staff/reconciler/retry/${eventId}`)
      // Reload um aktualisierten Sync-Status zu zeigen
      const r = await keycloakApi!.get(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/access`
      )
      setAccessMembers(r.data?.members ?? [])
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setAccessError(err.response?.data?.error ?? 'Retry fehlgeschlagen.')
    } finally {
      setRetryPendingEventId(null)
    }
  }

  // TQ #5897 — Sync-Status-Icon plus optionaler Retry-Knopf.
  // Mapping:
  //   undefined/null   -> grau '–'  (nie reconciled, war auch nicht
  //                       Toggle-betroffen)
  //   ''               -> Spinner   (event existiert, Worker noch nicht durch)
  //   'success'        -> ✓ gruen
  //   'needs_attention'-> ⚠ gelb
  //   'error'          -> ✕ rot + Retry-Button
  function renderSyncStatus(m: AccessMember) {
    const s = m.sync_status
    if (s == null) {
      return <span style={{ color: '#d1d5db', fontSize: '0.75rem' }}>–</span>
    }
    if (s === '') {
      return <span style={{ color: '#6b7280' }} title="Pending (Reconciler-Worker arbeitet)">⏳</span>
    }
    if (s === 'success') {
      return <span style={{ color: '#10b981' }} title="Sync erfolgreich">✓</span>
    }
    if (s === 'needs_attention') {
      return <span style={{ color: '#f59e0b' }} title="Staff-Aktion erforderlich">⚠</span>
    }
    if (s === 'error') {
      const retrying = retryPendingEventId === m.last_event_id
      const count = m.retry_count ?? 0
      const tip = `Sync fehlgeschlagen nach ${count} Versuch${count === 1 ? '' : 'en'} — klick fuer Manual-Retry`
      return (
        <button
          type="button"
          onClick={() => m.last_event_id && retryEvent(m.last_event_id)}
          disabled={retrying || !m.last_event_id}
          title={tip}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            color: '#dc2626', fontSize: '1rem', padding: 0,
            opacity: retrying ? 0.5 : 1,
          }}
        >
          {retrying ? '↻' : '✕'}
        </button>
      )
    }
    return <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{s}</span>
  }

  async function reloadMailboxes() {
    try {
      const r = await keycloakApi!.get(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes`
      )
      setMailboxes(r.data?.mailboxes ?? [])
      setMailDomain(r.data?.domain ?? '')
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setMailboxesError(err.response?.data?.error ?? 'Postfächer konnten nicht geladen werden.')
    }
  }

  async function doCreateMailbox() {
    const local = createLocal.trim().toLowerCase()
    if (!local) return
    setCreatePending(true)
    setMailboxesError(null)
    try {
      const r = await keycloakApi!.post(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes`,
        { local_part: local, display_name: createDisplay.trim() }
      )
      setCreatedInfo(r.data as CreateMailboxResp)
      setCreateLocal('')
      setCreateDisplay('')
      setShowCreate(false)
      await reloadMailboxes()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setMailboxesError(err.response?.data?.error ?? 'Anlegen fehlgeschlagen.')
    } finally {
      setCreatePending(false)
    }
  }

  async function doDeleteMailbox(address: string) {
    if (!window.confirm(`Postfach "${address}" wirklich löschen? Alle Mails gehen verloren.`)) return
    setDeletePending(address)
    setMailboxesError(null)
    try {
      await keycloakApi!.delete(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes/${encodeURIComponent(address)}`
      )
      await reloadMailboxes()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setMailboxesError(err.response?.data?.error ?? 'Löschen fehlgeschlagen.')
    } finally {
      setDeletePending(null)
    }
  }

  async function doResetPassword(address: string) {
    if (!window.confirm(`Passwort für "${address}" zurücksetzen? Das aktuelle Passwort wird ungültig.`)) return
    setResetPending(address)
    setMailboxesError(null)
    try {
      const r = await keycloakApi!.put(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes/${encodeURIComponent(address)}`,
        { reset_password: true }
      )
      const pw = (r.data?.new_password as string) || ''
      if (pw) setResetInfo({ address, password: pw })
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setMailboxesError(err.response?.data?.error ?? 'Reset fehlgeschlagen.')
    } finally {
      setResetPending(null)
    }
  }

  // Mailbox-Edit (Quota + Aliases) — TQ #5107 Phase 2
  function openEditMailbox(m: Mailbox) {
    setEditMailbox(m)
    setEditError(null)
    setEditQuotaInput(m.quota_bytes ? String(Math.round(m.quota_bytes / 1024 / 1024 / 1024)) : '')
    // Aliase: eine Zeile pro Alias damit der Editor einfach bleibt.
    setEditAliasesText((m.aliases ?? []).join('\n'))
  }
  async function doSaveMailboxEdit() {
    if (!editMailbox) return
    const aliases = editAliasesText.split('\n').map((s) => s.trim()).filter((s) => s !== '')
    const quotaGb = editQuotaInput.trim()
    const body: { quota_bytes?: number; aliases: string[] } = { aliases }
    if (quotaGb === '') {
      body.quota_bytes = 0 // 0 = unbegrenzt
    } else {
      const n = Number(quotaGb)
      if (!Number.isFinite(n) || n < 0) {
        setEditError('Quota muss eine positive Zahl in GB sein (oder leer für unbegrenzt).')
        return
      }
      body.quota_bytes = Math.round(n * 1024 * 1024 * 1024)
    }
    setEditPending(true)
    setEditError(null)
    try {
      await keycloakApi!.put(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes/${encodeURIComponent(editMailbox.address)}`,
        body
      )
      // Reload Mailbox-Liste
      const r = await keycloakApi!.get(
        `/api/org/${orgSlug}/services/${encodeURIComponent(planId)}/mailboxes`
      )
      setMailboxes(r.data?.mailboxes ?? [])
      setEditMailbox(null)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setEditError(err.response?.data?.error ?? 'Speichern fehlgeschlagen.')
    } finally {
      setEditPending(false)
    }
  }

  const fmtUptime = (v?: number | null) =>
    v == null ? '–' : `${(v * 100).toFixed(2)} %`
  const fmtLatency = (v?: number | null) =>
    v == null ? '–' : `${(v * 1000).toFixed(0)} ms`
  const fmtDate = (s?: string) =>
    s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

  return (
    <div style={{
      background: '#f3f4f6',
      padding: '1.25rem 1.5rem 1.5rem 3rem',
      borderTop: '1px solid #e5e7eb',
      borderLeft: '3px solid #3b82f6',
      borderBottom: '2px solid #d1d5db', // visuelle Trennung zur naechsten Service-Zeile
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)',
    }}>
      {/* Tab-Bar — Pill-Style */}
      <div style={{
        display: 'inline-flex', gap: '0.25rem', padding: '0.25rem',
        background: '#e5e7eb', borderRadius: 10, marginBottom: '1.25rem',
      }}>
        {(([
          ['overview', 'ℹ️', 'Übersicht'],
          ['health', '📡', 'Verfügbarkeit'],
          ['activity', '🕓', 'Aktivität'],
          ...(isMailService ? [['mailboxes', '📬', 'Postfächer']] : []),
          ...(isStorageService ? [['teams', '👥', 'Teams & Ordner']] : []),
          ...(isERPService ? [['erpnext', '🧾', 'ERPNext']] : []),
          ...(isERPService ? [['erp_users', '👤', 'User & Rollen']] : []),
          ...(isWebService ? [['web_admins', '🌐', 'WP-Admins']] : []),
          ...(isAriaService ? [['aria_quota', '🤖', 'Token-Budget']] : []),
          // Zugriff-Tab nur fuer Services wo es Sinn macht — nicht fuer BASE
          // (= Org-Mitgliedschaft, im Mitglieder-Tab gesteuert) und nicht
          // fuer Storage (granular via Teams-Tab).
          ...(!isBaseService && !isStorageService ? [['access', '🔑', 'Zugriff']] : []),
        ] as [Tab, string, string][])).map(([k, icon, label]) => {
          const active = tab === k
          return (
            <button
              key={k}
              type="button"
              onClick={(e) => { e.stopPropagation(); setTab(k) }}
              style={{
                padding: '0.5rem 0.875rem', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: '0.375rem',
                background: active ? '#fff' : 'transparent',
                color: active ? '#1f2937' : '#6b7280',
                fontWeight: active ? 600 : 500, fontSize: '0.8125rem',
                border: 'none', borderRadius: 7,
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#1f2937' }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#6b7280' }}
            >
              <span aria-hidden>{icon}</span>
              {label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <p style={{ color: '#6b7280', margin: 0 }}>Lade…</p>
      ) : !detail ? (
        <p style={{ color: '#6b7280', margin: 0 }}>Keine Details verfügbar.</p>
      ) : tab === 'overview' ? (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '0.75rem', alignItems: 'start',
        }}>
          {detail.description && (
            <div style={{ gridColumn: '1 / -1', color: '#4b5563', lineHeight: 1.5 }}>
              {detail.description}
            </div>
          )}
          <Field k="Plan-Code" v={<code>{detail.plan_id}</code>} />
          <Field k="Item-Code" v={<code>{detail.item_code || '–'}</code>} />
          <Field k="Item-Name" v={detail.item_name || '–'} />
          <Field k="Variante" v={detail.variant_name || '–'} />
          <Field k="Kategorie" v={detail.portal_category || '–'} />
          <Field
            k="Preis"
            v={detail.rate > 0
              ? `${detail.rate.toFixed(2)} ${detail.currency} / ${detail.billing_interval}`
              : 'Inklusive'}
          />
          <Field k="Menge" v={detail.qty.toString()} />
          <Field k="Aktiv seit" v={fmtDate(detail.start_date)} />
          <Field
            k="Subscription"
            v={
              <a
                href={`https://erp.it-sce.de/app/subscription/${detail.erpnext_subscription_id}`}
                target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#3b82f6', textDecoration: 'none' }}
              >
                {detail.erpnext_subscription_id} ↗
              </a>
            }
          />
          {detail.probes.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem',
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Endpunkte
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {detail.probes.map((p) => (
                  <li key={p.url}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.875rem' }}>
                      {p.label || p.url} ↗
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : tab === 'health' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {healthProbes === null ? (
            <p style={{ color: '#6b7280', margin: 0 }}>Lade Verfügbarkeitsdaten…</p>
          ) : healthProbes.length === 0 ? (
            <div style={{ padding: '0.75rem 1rem', background: '#fff', borderRadius: 6,
              color: '#6b7280', border: '1px solid #e5e7eb' }}>
              Für diesen Service sind noch keine Probe-URLs konfiguriert.
              Wenden Sie sich an IT-SCE um Verfügbarkeitsmonitoring zu aktivieren.
            </div>
          ) : (
            healthProbes.map((p) => (
              <div key={p.url} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{p.label || p.url}</div>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#6b7280', fontSize: '0.75rem', textDecoration: 'none' }}>
                      {p.url} ↗
                    </a>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                    background: p.up == null ? '#e5e7eb' : p.up ? '#d1fae5' : '#fee2e2',
                    color:      p.up == null ? '#6b7280' : p.up ? '#065f46' : '#991b1b',
                  }}>
                    {p.up == null ? 'unbekannt' : p.up ? '● erreichbar' : '● Ausfall'}
                  </span>
                </div>

                {/* Uptime-Kuma-Style Status-Bar — letzte 24h, 90 Buckets a 16min */}
                <StatusBar bars={p.bars} />

                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem',
                  marginTop: '0.875rem', fontSize: '0.8125rem',
                }}>
                  <Stat label="24h" value={fmtUptime(p.uptime_24h)} />
                  <Stat label="7 Tage" value={fmtUptime(p.uptime_7d)} />
                  <Stat label="30 Tage" value={fmtUptime(p.uptime_30d)} />
                  <Stat label="Latenz" value={fmtLatency(p.latency_sec)} />
                </div>
              </div>
            ))
          )}
        </div>
      ) : tab === 'erpnext' ? (
        // ERPNext-Tab Content (TQ #5106). Phase 1: Plan-Metadaten + Stub-Daten.
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {erpnextLoading && (
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Lade ERPNext-Daten…</div>
          )}
          {erpnextError && (
            <div style={{
              padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem',
            }}>{erpnextError}</div>
          )}
          {erpnext && (
            <>
              {erpnext.data_source === 'stub' && (
                <div style={{
                  padding: '0.625rem 0.875rem', background: '#fffbeb', border: '1px solid #fde68a',
                  borderRadius: 8, color: '#92400e', fontSize: '0.8125rem',
                }}>
                  {erpnext.note ?? 'ERPNext-Daten noch nicht verfuegbar — Phase 2 wired die Live-API an.'}
                </div>
              )}
              <div style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem',
                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '0.875rem 1rem', fontSize: '0.875rem',
              }}>
                <div style={{ color: '#6b7280' }}>ERPNext-URL</div>
                <div>
                  <a href={erpnext.erpnext_url} target="_blank" rel="noreferrer"
                     style={{ color: '#2563eb' }}>{erpnext.erpnext_url}</a>
                </div>
                <div style={{ color: '#6b7280' }}>ERPNext-Version</div>
                <div>{erpnext.erpnext_version ?? '—'}</div>
                <div style={{ color: '#6b7280' }}>Frappe-Version</div>
                <div>{erpnext.frappe_version ?? '—'}</div>
                <div style={{ color: '#6b7280' }}>Letzter Migration-Run</div>
                <div>
                  {erpnext.last_migration_at ?? '—'}
                  {erpnext.last_migration_ok === true && <span style={{ color: '#059669', marginLeft: '0.5rem' }}>✓</span>}
                  {erpnext.last_migration_ok === false && <span style={{ color: '#b91c1c', marginLeft: '0.5rem' }}>✗</span>}
                </div>
                <div style={{ color: '#6b7280' }}>Aktive User-Sessions</div>
                <div>{erpnext.active_sessions ?? '—'}</div>
                <div style={{ color: '#6b7280' }}>Custom DocTypes</div>
                <div>{erpnext.custom_doctypes ?? '—'}</div>
                <div style={{ color: '#6b7280' }}>Letzter Backup</div>
                <div>
                  {erpnext.last_backup_at ?? '—'}
                  {erpnext.last_backup_ok === true && <span style={{ color: '#059669', marginLeft: '0.5rem' }}>✓</span>}
                  {erpnext.last_backup_ok === false && <span style={{ color: '#b91c1c', marginLeft: '0.5rem' }}>✗</span>}
                </div>
              </div>
            </>
          )}
        </div>
      ) : tab === 'erp_users' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {erpUsersError && (
            <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem' }}>{erpUsersError}</div>
          )}
          {erpUsers && (
            <>
              {erpUsers.data_source === 'stub' && (
                <div style={{ padding: '0.625rem 0.875rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: '0.8125rem' }}>
                  {erpUsers.note ?? 'Frappe User-API noch nicht verdrahtet (Phase 2). Edit via ERPNext-UI /app/user.'}
                </div>
              )}
              {erpUsers.users.length === 0 ? (
                <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>Keine User geladen.</p>
              ) : (
                <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>E-Mail</th><th style={{ padding: '0.5rem' }}>Name</th>
                    <th style={{ padding: '0.5rem' }}>Aktiv</th><th style={{ padding: '0.5rem' }}>Rollen</th>
                  </tr></thead>
                  <tbody>{erpUsers.users.map((u) => (
                    <tr key={u.email} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem' }}>{u.email}</td>
                      <td style={{ padding: '0.5rem' }}>{u.full_name}</td>
                      <td style={{ padding: '0.5rem' }}>{u.enabled ? '✓' : '—'}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>{u.roles.join(', ')}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </div>
      ) : tab === 'web_admins' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {webAdminsError && (
            <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem' }}>{webAdminsError}</div>
          )}
          {webAdmins && (
            <>
              {webAdmins.data_source === 'stub' && (
                <div style={{ padding: '0.625rem 0.875rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: '0.8125rem' }}>
                  {webAdmins.note ?? 'WP-REST-API noch nicht verdrahtet (Phase 2).'}
                </div>
              )}
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                Site: <a href={webAdmins.site_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{webAdmins.site_url}</a>
              </p>
              {webAdmins.admins.length === 0 ? (
                <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>Keine Admins geladen.</p>
              ) : (
                <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>Login</th><th style={{ padding: '0.5rem' }}>Name</th>
                    <th style={{ padding: '0.5rem' }}>E-Mail</th><th style={{ padding: '0.5rem' }}>Rolle</th>
                  </tr></thead>
                  <tbody>{webAdmins.admins.map((u) => (
                    <tr key={u.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem' }}>{u.login}</td>
                      <td style={{ padding: '0.5rem' }}>{u.name}</td>
                      <td style={{ padding: '0.5rem' }}>{u.email}</td>
                      <td style={{ padding: '0.5rem' }}>{u.role}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </div>
      ) : tab === 'aria_quota' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {ariaQuotaError && (
            <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem' }}>{ariaQuotaError}</div>
          )}
          {ariaQuota && (
            <>
              {ariaQuota.data_source === 'stub' && (
                <div style={{ padding: '0.625rem 0.875rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: '0.8125rem' }}>
                  {ariaQuota.note ?? 'Per-User-Quota braucht Cortex-API-Erweiterung (Phase 2).'}
                </div>
              )}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.875rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
                <div style={{ color: '#6b7280' }}>Tenant-Budget (Monat)</div>
                <div>{ariaQuota.tenant_budget_daily > 0
                  ? ariaQuota.tenant_budget_daily.toLocaleString('de-DE')
                  : '—'} Tokens</div>
                <div style={{ color: '#6b7280' }}>Diesen Monat verbraucht</div>
                <div>{ariaQuota.tenant_used_today.toLocaleString('de-DE')} Tokens</div>
              </div>
              {ariaQuota.users.length === 0 ? (
                <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>Keine Per-User-Daten verfügbar.</p>
              ) : (
                <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>User</th>
                    <th style={{ padding: '0.5rem' }}>Heute</th>
                    <th style={{ padding: '0.5rem' }}>Gesamt</th>
                    <th style={{ padding: '0.5rem' }}>Limit/Tag</th>
                    <th style={{ padding: '0.5rem', width: '1%' }}></th>
                  </tr></thead>
                  <tbody>{ariaQuota.users.map((u) => (
                    <tr key={u.user_sub} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem' }}>{u.user_email}</td>
                      <td style={{ padding: '0.5rem' }}>{u.tokens_today.toLocaleString('de-DE')}</td>
                      <td style={{ padding: '0.5rem' }}>{u.tokens_total.toLocaleString('de-DE')}</td>
                      {ariaEditSub === u.user_sub ? (
                        <>
                          <td style={{ padding: '0.5rem' }}>
                            <input
                              type="number" min={0} step={100000}
                              value={ariaEditValue}
                              onChange={(e) => setAriaEditValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="leer = kein Limit"
                              disabled={ariaSaving}
                              style={{ width: '9rem', padding: '0.25rem 0.4rem', fontSize: '0.8125rem', border: '1px solid #d1d5db', borderRadius: 4 }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                            <button type="button" disabled={ariaSaving}
                              onClick={(e) => { e.stopPropagation(); saveAriaUserLimit(u.user_sub) }}
                              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8125rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: '0.35rem' }}>
                              {ariaSaving ? '…' : 'Speichern'}
                            </button>
                            <button type="button" disabled={ariaSaving}
                              onClick={(e) => { e.stopPropagation(); setAriaEditSub(null); setAriaEditValue('') }}
                              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8125rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
                              Abbrechen
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '0.5rem' }}>{u.per_user_limit ? `${u.per_user_limit.toLocaleString('de-DE')} Tokens` : '—'}</td>
                          <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); setAriaEditSub(u.user_sub); setAriaEditValue(u.per_user_limit ? String(u.per_user_limit) : ''); setAriaQuotaError(null) }}
                              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8125rem', background: '#fff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 4, cursor: 'pointer' }}>
                              Limit
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </div>
      ) : tab === 'mailboxes' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {mailboxesError && (
            <div style={{
              padding: '0.75rem 1rem', background: '#fef2f2', color: '#991b1b',
              borderRadius: 6, border: '1px solid #fecaca',
            }}>{mailboxesError}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            {mailDomain ? (
              <div style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                Mail-Domain: <code>{mailDomain}</code>
                {mailboxes !== null && ` • ${mailboxes.length} Postfach${mailboxes.length === 1 ? '' : 'fächer'}`}
              </div>
            ) : <span />}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowCreate(true) }}
              disabled={!mailDomain}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: 6,
                background: mailDomain ? '#10b981' : '#d1d5db',
                color: '#fff', cursor: mailDomain ? 'pointer' : 'not-allowed',
                fontSize: '0.8125rem', fontWeight: 600,
              }}>+ Postfach anlegen</button>
          </div>
          {mailboxes === null ? (
            <p style={{ color: '#6b7280', margin: 0 }}>Lade Postfächer…</p>
          ) : mailboxes.length === 0 ? (
            <div style={{ padding: '0.75rem 1rem', background: '#fff', borderRadius: 6,
              color: '#6b7280', border: '1px solid #e5e7eb' }}>
              Keine Postfächer für diese Mail-Domain gefunden.
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem' }}>Adresse</th>
                    <th style={{ padding: '0.5rem 0.75rem' }}>Name</th>
                    <th style={{ padding: '0.5rem 0.75rem' }}>Aliases</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Quota</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {mailboxes.map((m) => (
                    <tr key={m.address} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <code style={{ fontSize: '0.8125rem' }}>{m.address}</code>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#1f2937' }}>
                        {m.display_name || <span style={{ color: '#9ca3af' }}>–</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', fontSize: '0.8125rem' }}>
                        {m.aliases && m.aliases.length > 0 ? m.aliases.join(', ') : <span style={{ color: '#9ca3af' }}>–</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums', color: m.quota_bytes ? '#1f2937' : '#9ca3af' }}>
                        {m.quota_bytes ? formatBytes(m.quota_bytes) : 'unbegrenzt'}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openEditMailbox(m) }}
                            title="Quota & Aliase bearbeiten"
                            style={{
                              padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4,
                              background: '#fff', cursor: 'pointer', fontSize: '0.75rem', color: '#1f2937',
                            }}>✎ Bearbeiten</button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void doResetPassword(m.address) }}
                            disabled={resetPending === m.address}
                            title="Passwort zurücksetzen"
                            style={{
                              padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4,
                              background: '#fff', cursor: 'pointer', fontSize: '0.75rem', color: '#1f2937',
                            }}>🔑 PW-Reset</button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void doDeleteMailbox(m.address) }}
                            disabled={deletePending === m.address}
                            title="Postfach löschen"
                            style={{
                              padding: '3px 8px', border: '1px solid #fecaca', borderRadius: 4,
                              background: '#fef2f2', cursor: 'pointer', fontSize: '0.75rem', color: '#991b1b',
                            }}>🗑 Löschen</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
            Quota und Aliase via ✎ Bearbeiten. Out-of-Office folgt (TQ #5107c, Stalwart-Side TBD).
          </p>

          {/* Edit-Mailbox Modal (Quota + Aliases) — TQ #5107 Phase 2 */}
          {editMailbox && (
            <div onClick={() => { if (!editPending) setEditMailbox(null) }}
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 8, padding: '1.25rem 1.5rem', minWidth: 440, maxWidth: 540 }}>
                <h3 style={{ marginTop: 0 }}>Postfach bearbeiten</h3>
                <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                  {editMailbox.address}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#374151' }}>Quota (GB) — leer = unbegrenzt</span>
                    <input value={editQuotaInput} onChange={(e) => setEditQuotaInput(e.target.value)}
                      placeholder="z.B. 5" inputMode="decimal"
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#374151' }}>Aliase (eine Adresse pro Zeile, gleiche Domain)</span>
                    <textarea value={editAliasesText} onChange={(e) => setEditAliasesText(e.target.value)}
                      placeholder={`info@${mailDomain}\nkontakt@${mailDomain}`}
                      rows={4}
                      style={{
                        padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4,
                        fontSize: '0.875rem', fontFamily: 'monospace', resize: 'vertical',
                      }} />
                  </label>
                </div>
                {editError && (
                  <div style={{
                    padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: 6, color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.75rem',
                  }}>{editError}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setEditMailbox(null)} disabled={editPending}
                    style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
                    Abbrechen
                  </button>
                  <button type="button" onClick={() => void doSaveMailboxEdit()} disabled={editPending}
                    style={{
                      padding: '6px 12px', border: 'none', borderRadius: 6,
                      background: '#10b981', color: '#fff',
                      cursor: editPending ? 'wait' : 'pointer', fontSize: '0.875rem', fontWeight: 600,
                    }}>
                    {editPending ? 'Speichere …' : 'Speichern'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Create-Mailbox Modal */}
          {showCreate && (
            <div onClick={() => setShowCreate(false)}
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 8, padding: '1.25rem 1.5rem', minWidth: 400, maxWidth: 480 }}>
                <h3 style={{ marginTop: 0 }}>Postfach anlegen</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#374151' }}>Local-Part (vor @)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input value={createLocal} onChange={(e) => setCreateLocal(e.target.value)}
                        placeholder="z.B. info" autoFocus
                        style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem' }} />
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>@{mailDomain}</span>
                    </div>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#374151' }}>Anzeigename (optional)</span>
                    <input value={createDisplay} onChange={(e) => setCreateDisplay(e.target.value)}
                      placeholder="z.B. Info Postfach"
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem' }} />
                  </label>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 1rem' }}>
                  Ein Initial-Passwort wird automatisch generiert und einmalig nach Anlage angezeigt.
                  Beim ersten Login bitten Sie den Mitarbeiter, es zu ändern.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setShowCreate(false)} disabled={createPending}
                    style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
                    Abbrechen
                  </button>
                  <button type="button" onClick={doCreateMailbox} disabled={createPending || !createLocal.trim()}
                    style={{
                      padding: '6px 12px', border: 'none', borderRadius: 6,
                      background: !createLocal.trim() ? '#d1d5db' : '#10b981', color: '#fff',
                      cursor: createPending ? 'wait' : 'pointer', fontSize: '0.875rem', fontWeight: 600,
                    }}>
                    {createPending ? 'Wird angelegt …' : 'Anlegen'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Created-Mailbox Info-Modal (Passwort einmalig anzeigen) */}
          {createdInfo && (
            <div onClick={() => setCreatedInfo(null)}
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
              }}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 8, padding: '1.25rem 1.5rem', minWidth: 400, maxWidth: 520 }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>✓ Postfach angelegt</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                    <strong>Adresse:</strong> <code>{createdInfo.address}</code>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                    <strong>Initial-Passwort:</strong>
                  </div>
                  <div style={{
                    padding: '0.5rem 0.75rem', background: '#fef3c7', borderRadius: 4,
                    fontFamily: 'monospace', fontSize: '0.9375rem', userSelect: 'all',
                    border: '1px solid #fbbf24', wordBreak: 'break-all',
                  }}>{createdInfo.initial_password}</div>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0 0 1rem',
                  padding: '0.5rem 0.75rem', background: '#fef3c7', borderRadius: 4 }}>
                  ⚠ Dieses Passwort wird nicht gespeichert. Bitte jetzt kopieren und sicher an den
                  Mitarbeiter weitergeben. Beim ersten Login durch den Mitarbeiter ändern lassen.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setCreatedInfo(null)}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                    Verstanden, Passwort wurde notiert
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reset-Password Info-Modal */}
          {resetInfo && (
            <div onClick={() => setResetInfo(null)}
              style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
              }}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 8, padding: '1.25rem 1.5rem', minWidth: 400, maxWidth: 520 }}>
                <h3 style={{ marginTop: 0, color: '#1e40af' }}>🔑 Passwort zurückgesetzt</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                    <strong>Adresse:</strong> <code>{resetInfo.address}</code>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                    <strong>Neues Passwort:</strong>
                  </div>
                  <div style={{
                    padding: '0.5rem 0.75rem', background: '#dbeafe', borderRadius: 4,
                    fontFamily: 'monospace', fontSize: '0.9375rem', userSelect: 'all',
                    border: '1px solid #93c5fd', wordBreak: 'break-all',
                  }}>{resetInfo.password}</div>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#1e40af', margin: '0 0 1rem',
                  padding: '0.5rem 0.75rem', background: '#dbeafe', borderRadius: 4 }}>
                  ⚠ Dieses Passwort wird nicht gespeichert. Bitte jetzt kopieren und sicher an den
                  Mitarbeiter weitergeben.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setResetInfo(null)}
                    style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                    Verstanden, Passwort wurde notiert
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : tab === 'teams' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{
            padding: '0.625rem 0.875rem', background: '#dbeafe', color: '#1e40af',
            borderRadius: 6, fontSize: '0.8125rem',
          }}>
            <strong>Fach-Bereiche steuern Ordner-Zugriffe.</strong> Jedes Team
            mapped auf eine Nextcloud-Group; Group-Folders sind in NC pro Team
            mit Lese-/Schreibrechten verknüpft. Toggle setzt die KC-Group
            <code style={{ marginLeft: 4 }}>/teams/&lt;name&gt;</code> — Wirkung
            beim nächsten OIDC-Login in NC.
          </div>

          {/* Group-Folder-Liste (TQ #5121 Phase 1) — Stub solange NC OCS-API nicht verdrahtet. */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.875rem 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.9375rem', color: '#1f2937' }}>📁 Group-Folder (Nextcloud)</h4>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Read-only Vorschau</span>
            </div>
            {storageFolders === null && !storageFoldersError && (
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Lade Folders…</p>
            )}
            {storageFoldersError && (
              <div style={{
                padding: '0.5rem 0.75rem', background: '#fef2f2', color: '#991b1b',
                borderRadius: 6, border: '1px solid #fecaca', fontSize: '0.8125rem',
              }}>{storageFoldersError}</div>
            )}
            {storageFolders && storageFolders.data_source === 'stub' && (
              <div style={{
                padding: '0.5rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a',
                borderRadius: 6, color: '#92400e', fontSize: '0.8125rem',
              }}>
                {storageFolders.note ?? 'NC OCS-API noch nicht verdrahtet (Phase 2). Edit via occ groupfolders:list auf NC-Pod.'}
              </div>
            )}
            {storageFolders && storageFolders.folders.length > 0 && (
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '0.375rem 0.5rem' }}>Name</th>
                    <th style={{ padding: '0.375rem 0.5rem' }}>Quota</th>
                    <th style={{ padding: '0.375rem 0.5rem' }}>Teams (ACL)</th>
                  </tr>
                </thead>
                <tbody>
                  {storageFolders.folders.map((f) => (
                    <tr key={f.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem' }}>{f.name}</td>
                      <td style={{ padding: '0.5rem', fontVariantNumeric: 'tabular-nums' }}>
                        {f.quota_bytes ? formatBytes(f.quota_bytes) : '—'}
                      </td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {f.acls.map((a) => `${a.team} (${[
                          a.read && 'r', a.update && 'w', a.share && 's', a.delete && 'd',
                        ].filter(Boolean).join('')})`).join(', ') || <span style={{ color: '#9ca3af' }}>keine</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {teamsError && (
            <div style={{
              padding: '0.75rem 1rem', background: '#fef2f2', color: '#991b1b',
              borderRadius: 6, border: '1px solid #fecaca',
            }}>{teamsError}</div>
          )}
          {teams === null ? (
            <p style={{ color: '#6b7280', margin: 0 }}>Lade Teams…</p>
          ) : teams.length === 0 ? (
            <div style={{ padding: '0.75rem 1rem', background: '#fff', borderRadius: 6,
              color: '#6b7280', border: '1px solid #e5e7eb' }}>
              Keine Teams definiert. Anlegen via Keycloak (Subgroup unter <code>/teams/</code>).
            </div>
          ) : (
            teams.map((t) => (
              <div key={t.id} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '0.625rem 0.875rem', background: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{t.name}</span>
                    <code style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}>{t.path}</code>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {t.members.filter(m => m.has_membership).length} / {t.members.length} Mitglieder
                  </span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <tbody>
                    {t.members.map((m) => {
                      const name = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || displayIdentity(m).split('@')[0]
                      const key = `${t.id}:${m.keycloak_id}`
                      const pending = teamTogglePending === key
                      return (
                        <tr key={m.keycloak_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.375rem 0.875rem' }}>
                            {name}
                            <span style={{ marginLeft: 6, fontSize: '0.6875rem', color: '#9ca3af' }}>
                              {isItsceServiceAccount(m) ? '' : m.email}
                            </span>
                          </td>
                          <td style={{ padding: '0.375rem 0.875rem', textAlign: 'right', width: 130 }}>
                            <label style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              cursor: 'pointer', opacity: pending ? 0.6 : 1,
                            }}>
                              <input
                                type="checkbox"
                                checked={m.has_membership}
                                disabled={pending}
                                onChange={() => toggleTeamMember(t.id, m)}
                              />
                              <span style={{
                                fontSize: '0.75rem', fontWeight: 600,
                                color: m.has_membership ? '#10b981' : '#6b7280',
                              }}>
                                {pending ? '…' : m.has_membership ? 'im Team' : 'kein Zugriff'}
                              </span>
                            </label>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
            Welche Group-Folders an welches Team hängen, siehst du heute noch nicht hier —
            Direktblick via <code>occ groupfolders:list</code> auf der NC-Konsole. Folder-Verwaltung
            kommt mit nächstem Sprint.
          </p>
        </div>
      ) : tab === 'access' ? (
        (() => {
          if (isBaseService) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.875rem 1rem', background: '#fef3c7', color: '#92400e',
                  borderRadius: 6, fontSize: '0.875rem', border: '1px solid #fde68a',
                }}>
                  <strong>BASE-Zugriff = Org-Mitgliedschaft.</strong> Jedes Org-
                  Mitglied hat automatisch Zugriff auf Bitwarden + Keycloak-Login.
                  Steuern: <em>Mitglieder-Tab → Mitglied einladen / entfernen</em>.
                  Kein separater Service-Toggle hier sinnvoll.
                </div>
              </div>
            )
          }
          return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {accessError && (
            <div style={{
              padding: '0.75rem 1rem', background: '#fef2f2', color: '#991b1b',
              borderRadius: 6, border: '1px solid #fecaca',
            }}>{accessError}</div>
          )}
          {accessMessage && (
            <div style={{
              padding: '0.625rem 0.875rem', background: '#dbeafe', color: '#1e40af',
              borderRadius: 6, fontSize: '0.8125rem',
            }}>{accessMessage}</div>
          )}
          {accessGroupPath && (
            <div style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
              Keycloak-Gruppe: <code>{accessGroupPath}</code>
              {accessMembers !== null && ` • ${accessMembers.filter(m => m.has_access).length} von ${accessMembers.length} Mitgliedern berechtigt`}
            </div>
          )}
          {accessMembers === null ? (
            <p style={{ color: '#6b7280', margin: 0 }}>Lade Zugriffsliste…</p>
          ) : accessMembers.length === 0 ? (
            !accessMessage && (
              <div style={{ padding: '0.75rem 1rem', background: '#fff', borderRadius: 6,
                color: '#6b7280', border: '1px solid #e5e7eb' }}>
                Keine Mitglieder in der Organisation.
              </div>
            )
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem' }}>Mitglied</th>
                    <th style={{ padding: '0.5rem 0.75rem' }}>E-Mail</th>
                    <th style={{ padding: '0.5rem 0.75rem' }}>Typ</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }} title="Reconciler-Sync-Status">Sync</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Service-Berechtigung</th>
                  </tr>
                </thead>
                <tbody>
                  {accessMembers.map((m) => {
                    const name = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || displayIdentity(m).split('@')[0]
                    const pending = accessTogglePending === m.keycloak_id
                    const syncIcon = renderSyncStatus(m)
                    return (
                      <tr key={m.keycloak_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          {name}
                          {m.is_owner && (
                            <span style={{ marginLeft: 6, fontSize: '0.6875rem', background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>
                              INHABER
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                          <code style={{ fontSize: '0.8125rem' }}>{displayIdentity(m)}</code>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <span style={{
                            fontSize: '0.6875rem', padding: '1px 8px', borderRadius: 3,
                            background: m.account_type === 'service' ? '#ede9fe' : '#dbeafe',
                            color: m.account_type === 'service' ? '#6d28d9' : '#1e40af',
                            fontWeight: 600,
                          }}>
                            {m.account_type === 'service' ? 'SERVICE' : 'PERSON'}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                          {syncIcon}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            cursor: m.is_owner ? 'not-allowed' : 'pointer',
                            opacity: pending ? 0.6 : 1,
                          }} title={m.is_owner ? 'Inhaber kann nicht entzogen werden' : 'Klicken um Berechtigung umzuschalten'}>
                            <input
                              type="checkbox"
                              checked={m.has_access || m.is_owner}
                              disabled={m.is_owner || pending}
                              onChange={() => toggleAccess(m)}
                            />
                            <span style={{
                              fontSize: '0.75rem', fontWeight: 600,
                              color: (m.has_access || m.is_owner) ? '#10b981' : '#6b7280',
                            }}>
                              {pending ? '…' : (m.has_access || m.is_owner) ? 'Berechtigt' : 'Nicht berechtigt'}
                            </span>
                          </label>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
            Toggle setzt die Keycloak-Gruppe. Nextcloud-Berechtigungen wirken beim nächsten Login automatisch.
            Mail, ERPNext und Bitwarden folgen mit Phase 3 (Reconciler — TQ #5111).
          </p>
        </div>
          )
        })()
      ) : (
        <div style={{ padding: '0.75rem 1rem', background: '#fff', borderRadius: 6,
          color: '#6b7280', border: '1px solid #e5e7eb' }}>
          Aktivitäts-Trail kommt mit dem nächsten Sprint
          (Audit-Events: Sub-Änderungen, Provisioning, Member-Aktionen).
        </div>
      )}
    </div>
  )
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div style={{ color: '#6b7280', fontSize: '0.6875rem', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{k}</div>
      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>{v}</div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#6b7280', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ color: '#1f2937', fontWeight: 600, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  )
}

/**
 * StatusBar — Uptime-Heatmap im Atlassian-Statuspage-Stil.
 *
 * 90 vertikale Balken ueber die letzten 24h. Footer zeigt "vor 24h" +
 * Uptime-Prozent in der Mitte + "jetzt".
 *
 * Farben:
 *   - gruen  = up      (avg >= 99%)
 *   - gelb   = partial (1% < avg < 99%)
 *   - rot    = down    (avg <= 1%)
 *   - grau   = unknown (Prometheus liefert keine Daten)
 */
function StatusBar({ bars }: { bars?: HealthBar[] }) {
  if (!bars || bars.length === 0) {
    return (
      <div style={{
        marginTop: '1rem', padding: '0.625rem 0.875rem',
        background: '#f9fafb', borderRadius: 6, border: '1px dashed #d1d5db',
        color: '#6b7280', fontSize: '0.75rem',
      }}>
        Keine Verlaufsdaten verfügbar — Prometheus-Targets sind noch nicht aktiv.
      </div>
    )
  }
  const colorOf = (s: HealthBar['status']) => {
    switch (s) {
      case 'up': return '#22c55e'      // green-500
      case 'partial': return '#f59e0b' // amber-500
      case 'down': return '#ef4444'    // red-500
      default: return '#d1d5db'        // gray-300
    }
  }
  const labelOf = (s: HealthBar['status']) => {
    switch (s) {
      case 'up': return 'erreichbar'
      case 'partial': return 'teilweise'
      case 'down': return 'Ausfall'
      default: return 'keine Daten'
    }
  }
  const fmtTime = (t: number) =>
    new Date(t * 1000).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    })

  // Uptime ueber alle bekannten Buckets (unknown wird ausgeklammert).
  const knownBars = bars.filter((b) => b.value != null)
  const uptimePct = knownBars.length === 0
    ? null
    : (knownBars.reduce((acc, b) => acc + (b.value as number), 0) / knownBars.length) * 100

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Bar-Reihe */}
      <div style={{
        display: 'flex', gap: 2, alignItems: 'stretch', height: 36,
        padding: '0 2px',
      }}>
        {bars.map((b, i) => (
          <div
            key={i}
            title={`${fmtTime(b.t)} — ${labelOf(b.status)}${b.value != null ? ` (${(b.value * 100).toFixed(0)}%)` : ''}`}
            style={{
              flex: 1,
              minWidth: 3,
              maxWidth: 8,
              background: colorOf(b.status),
              borderRadius: 2,
              opacity: b.status === 'unknown' ? 0.6 : 1,
              transition: 'transform 0.1s, filter 0.1s',
              cursor: 'help',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scaleY(1.15)'
              e.currentTarget.style.filter = 'brightness(1.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scaleY(1)'
              e.currentTarget.style.filter = 'none'
            }}
          />
        ))}
      </div>

      {/* Footer mit zentriertem Uptime-Prozent + Trennlinie */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
        marginTop: '0.5rem', gap: '0.75rem',
        color: '#6b7280', fontSize: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ whiteSpace: 'nowrap' }}>vor 24 h</span>
          <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>
        <div style={{ fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap' }}>
          {uptimePct == null ? '—' : `${uptimePct.toFixed(2)} % Uptime`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ whiteSpace: 'nowrap' }}>jetzt</span>
        </div>
      </div>
    </div>
  )
}
